'use client'

import {type RefCallback, useCallback, useLayoutEffect, useRef, useState, useSyncExternalStore} from 'react'
import {getActiveDocumentEditor, getDocumentScrollOwner, getEditorScrollOwner, getLocalEditorReturnPoint} from './documentEditorOwnership'

const ENTRY_KEY = '__fitroScrollEntry'
const STORAGE_KEY = 'fitro:scroll:v1'
const VIEW_STORAGE_KEY = 'fitro:view:v1'
const MAX_VIEW_STATES = 160
const MAX_ENTRIES = 80
const MAX_AGE = 8 * 60 * 60 * 1000
const RESTORE_TIMEOUT = 5000

type ScrollTarget = Window | HTMLElement
export interface ScrollPosition {x: number; y: number}
type SavedPosition = ScrollPosition & {at: number}
export interface NavigationHistoryEvent {
    kind: 'push' | 'replace' | 'pop'
    fromKey: string
    toKey: string
    fromUrl: string
    toUrl: string
}
const listeners = new Set<(event: NavigationHistoryEvent) => void>()
const beforeNavigation = new Set<() => void>()
let stopHistory: (() => void) | undefined
let writeHistoryEntry: History['replaceState'] | undefined
let currentKey = ''
let currentUrl = ''
let latestNavigation: NavigationHistoryEvent | undefined
let activeScope: string | undefined
let captureFrozen = false
let pendingEntryRestore: {key: string; url: string} | undefined
let positions: Record<string, SavedPosition> | undefined
const viewStates = new Map<string, {value: unknown; at: number}>()
let viewsLoaded = false

function newKey() {
    return typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

/** Opaque identity for a visit, independent of the URL (which may contain tokens). */
export function getHistoryEntryKey(): string {
    if (typeof window === 'undefined') return ''
    const key: unknown = window.history.state?.[ENTRY_KEY]
    if (typeof key === 'string') return key
    // Native fragment entries can have null state. Turning them into a
    // non-Next entry makes App Router reload on the next traversal.
    if (!window.history.state?.__NA) return ''
    const created = newKey()
    const replace = writeHistoryEntry ?? window.history.replaceState
    replace.call(window.history, {...window.history.state, [ENTRY_KEY]: created}, '')
    return created
}

/** The opener was captured before a routed page could shrink the document. */
export function getPushedEditorReturnPosition(): ScrollPosition | undefined {
    if (!activeScope || latestNavigation?.kind !== 'push' || latestNavigation.toKey !== getHistoryEntryKey()) return
    const snapshots = readPositions()
    return snapshots[storageKey(activeScope, latestNavigation.fromKey, 'editor')]
        ?? snapshots[storageKey(activeScope, latestNavigation.fromKey, 'document')]
}

/** Keep the original visit while the modal bootstrap replaces it with a temporary background. */
export function captureHistoryEntryForRestore(): string {
    const key = getHistoryEntryKey()
    // A direct desktop URL can be withheld while its background is rebuilt.
    // Its empty shell must not overwrite the editor snapshot saved before reload.
    const savedEditor = activeScope && readPositions()[storageKey(activeScope, key, 'editor')]
    if (!savedEditor || getActiveDocumentEditor()) for (const capture of beforeNavigation) capture()
    persist()
    if (!key) return key
    const temporaryKey = newKey()
    const replace = writeHistoryEntry ?? window.history.replaceState
    replace.call(window.history, {...window.history.state, [ENTRY_KEY]: temporaryKey}, '')
    currentKey = temporaryKey
    return key
}

/** Resume the visit carried by the desktop modal bootstrap's existing handoff. */
export function prepareHistoryEntryRestore(key: string, targetUrl: string) {
    const url = new URL(targetUrl, window.location.href)
    if (key && url.origin === window.location.origin) pendingEntryRestore = {key, url: url.href}
}

/** One shared History API observer; preserves all framework-owned state. */
export function observeNavigationHistory(listener: (event: NavigationHistoryEvent) => void) {
    listeners.add(listener)
    if (!stopHistory) {
        currentKey = getHistoryEntryKey()
        currentUrl = window.location.href
        const {pushState, replaceState} = window.history
        let observing = true
        writeHistoryEntry = replaceState
        const emit = (event: NavigationHistoryEvent) => {
            currentKey = event.toKey
            currentUrl = event.toUrl
            latestNavigation = event
            for (const subscriber of listeners) subscriber(event)
        }
        const push: History['pushState'] = function (this: History, data, unused, url) {
            if (!observing) return pushState.call(this, data, unused, url)
            for (const capture of beforeNavigation) capture()
            const fromKey = currentKey
            const fromUrl = currentUrl
            const pending = pendingEntryRestore
            pendingEntryRestore = undefined
            const restoring = pending && pending.url === new URL(url ?? window.location.href, window.location.href).href
            const toKey = restoring ? pending.key : newKey()
            pushState.call(this, {...data, [ENTRY_KEY]: toKey}, unused, url)
            captureFrozen = true
            emit({kind: restoring ? 'pop' : 'push', fromKey, toKey, fromUrl, toUrl: window.location.href})
        }
        const replace: History['replaceState'] = function (this: History, data, unused, url) {
            if (!observing) return replaceState.call(this, data, unused, url)
            const fromUrl = currentUrl
            const toUrl = url === null || url === undefined ? window.location.href : new URL(url, window.location.href).href
            // Next also replaces its tree state without navigating. Do not turn
            // those writes into a scroll reset or a new visit.
            const changed = toUrl !== fromUrl
            // Next may replace its tree during a Navigation API traversal
            // before popstate reaches us. The browser's entry already changed;
            // copying our previous key here would destroy the destination.
            const key = getHistoryEntryKey() || (data?.__NA ? newKey() : '')
            const traversing = key !== currentKey
            const fromKey = currentKey
            if (traversing) captureFrozen = true
            else if (changed) for (const capture of beforeNavigation) capture()
            replaceState.call(this, key ? {...data, [ENTRY_KEY]: key} : data, unused, url)
            if (changed || traversing) {
                captureFrozen = true
                emit({kind: traversing ? 'pop' : 'replace', fromKey, toKey: key, fromUrl, toUrl: window.location.href})
            }
        }
        const pop = () => {
            // scroll events keep the leaving snapshot current; reading window
            // here could already read native restoration for the destination.
            const toKey = getHistoryEntryKey()
            if (toKey === currentKey && currentUrl === window.location.href) return
            captureFrozen = true
            emit({kind: 'pop', fromKey: currentKey, toKey, fromUrl: currentUrl, toUrl: window.location.href})
        }
        const navigation = (window as Window & {navigation?: EventTarget}).navigation
        const onNavigate = (event: Event) => {
            if ((event as Event & {navigationType?: string}).navigationType === 'traverse') {
                for (const capture of beforeNavigation) capture()
                captureFrozen = true
            }
        }
        navigation?.addEventListener('navigate', onNavigate)
        window.history.pushState = push
        window.history.replaceState = replace
        window.addEventListener('popstate', pop, {capture: true})
        stopHistory = () => {
            // A framework wrapper may have captured ours. Make that old layer
            // inert even when it cannot safely be removed from the chain.
            observing = false
            if (window.history.pushState === push) window.history.pushState = pushState
            if (window.history.replaceState === replace) window.history.replaceState = replaceState
            window.removeEventListener('popstate', pop, {capture: true})
            navigation?.removeEventListener('navigate', onNavigate)
            stopHistory = undefined
            writeHistoryEntry = undefined
        }
    }
    return () => {
        listeners.delete(listener)
        if (!listeners.size) stopHistory?.()
    }
}

function readPositions() {
    if (positions) return positions
    positions = {}
    try {
        const parsed: unknown = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '{}')
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            for (const [key, value] of Object.entries(parsed)) {
                const point = value as SavedPosition | null
                if (point && Number.isFinite(point.x) && Number.isFinite(point.y) && Number.isFinite(point.at) &&
                    point.x >= 0 && point.y >= 0 && Date.now() - point.at < MAX_AGE) positions[key] = point
            }
        }
    } catch { /* Storage is optional, including Safari private browsing. */ }
    return positions
}

function storageKey(scope: string, entry: string, surface: string) {
    return JSON.stringify([scope, entry, surface])
}

function persist() {
    const recent = Object.entries(readPositions()).filter(([, point]) => Date.now() - point.at < MAX_AGE)
        .sort((a, b) => b[1].at - a[1].at).slice(0, MAX_ENTRIES)
    positions = Object.fromEntries(recent)
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions)) } catch { /* Memory remains available. */ }
}

export function readScrollPosition(target: ScrollTarget): ScrollPosition {
    return target === window ? {x: window.scrollX, y: Math.max(0, window.scrollY)} : {x: (target as HTMLElement).scrollLeft, y: Math.max(0, (target as HTMLElement).scrollTop)}
}

function surfaceTarget(): {target: ScrollTarget; surface: string} | null {
    const editor = getActiveDocumentEditor()
    if (editor) {
        const target = getEditorScrollOwner(editor)
        return target ? {target, surface: 'editor'} : null
    }
    return getDocumentScrollOwner() === document ? {target: window, surface: 'document'} : null
}

function saveCurrentPosition(knownSurface?: ReturnType<typeof surfaceTarget>) {
    if (!activeScope || captureFrozen) return
    const surface = knownSurface ?? surfaceTarget()
    if (!surface) return
    // A fixed-body lock has a synthetic zero position. Its owner will restore it.
    if (surface.target === window && document.documentElement.classList.contains('no-scroll')) return
    const entry = currentKey || getHistoryEntryKey()
    if (!entry) return
    const localReturn = getLocalEditorReturnPoint()
    const historySurface = localReturn?.owner === document ? 'document' : surface.surface
    // Local utilities disappear on reload; their existing return points retain
    // the route's position without introducing another overlay stack.
    delete readPositions()[storageKey(activeScope, entry, historySurface === 'editor' ? 'document' : 'editor')]
    savePosition(storageKey(activeScope, entry, historySurface), localReturn
        ? {x: localReturn.left, y: localReturn.top} : readScrollPosition(surface.target))
}

function savePosition(key: string, position: ScrollPosition) {
    const snapshots = readPositions()
    if (!(key in snapshots) && Object.keys(snapshots).length >= MAX_ENTRIES) {
        const oldest = Object.keys(snapshots).reduce((a, b) => snapshots[a].at < snapshots[b].at ? a : b)
        delete snapshots[oldest]
    }
    snapshots[key] = {...position, at: Date.now()}
}

/** Cancelable, bounded restoration. It never renders React during scrolling. */
export function restoreScrollPosition(
    target: ScrollTarget, position: ScrollPosition,
    {isReady = () => true, onFinish = () => {}, content, resolveTarget = () => target}: {isReady?: () => boolean; onFinish?: () => void; content?: Element | null; resolveTarget?: () => ScrollTarget | null} = {},
): () => void {
    let stopped = false
    let frame = 0
    let timeout = 0
    let retry = 0
    let observer: ResizeObserver | undefined
    const end = () => {
        if (stopped) return
        stopped = true
        cancelAnimationFrame(frame)
        clearTimeout(timeout)
        clearTimeout(retry)
        observer?.disconnect()
        window.removeEventListener('wheel', interrupt, true)
        window.removeEventListener('touchstart', interrupt, true)
        window.removeEventListener('pointerdown', interrupt, true)
        window.removeEventListener('keydown', keyInterrupt, true)
        window.removeEventListener('focusin', focusInterrupt, true)
        onFinish()
    }
    const interrupt = () => end()
    const focusInterrupt = (event: FocusEvent) => {
        // Editable focus can open a keyboard. Link/header focus is also used
        // by restoration itself; user Tab/pointer input is handled separately.
        if (event.target instanceof HTMLElement && event.target.matches('input, textarea, select, [contenteditable="true"]')) end()
    }
    const keyInterrupt = (event: KeyboardEvent) => {
        if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Tab'].includes(event.key)) end()
    }
    const attempt = () => {
        if (stopped) return
        const availableTarget = resolveTarget()
        if (availableTarget && isReady() && !(availableTarget === window && document.documentElement.classList.contains('no-scroll'))) {
            const element = availableTarget === window ? document.documentElement : availableTarget as HTMLElement
            const maxY = Math.max(0, (availableTarget === window
                ? Math.max(element.scrollHeight, document.body.scrollHeight) - window.innerHeight
                : element.scrollHeight - element.clientHeight))
            const maxX = Math.max(0, (availableTarget === window
                ? Math.max(element.scrollWidth, document.body.scrollWidth) - window.innerWidth
                : element.scrollWidth - element.clientWidth))
            // Growing content must not produce a scroll jump on every retry.
            // A shortened list must not cause an unsolicited jump at timeout.
            if (position.y <= maxY && position.x <= maxX) {
                availableTarget.scrollTo({top: Math.min(position.y, maxY), left: Math.min(position.x, maxX), behavior: 'instant'})
                const actual = readScrollPosition(availableTarget)
                if (Math.abs(actual.y - position.y) < 2 && Math.abs(actual.x - position.x) < 2) {
                    end()
                    return
                }
            }
        }
        // The low-frequency retry also covers DOM replacement of equal height,
        // which ResizeObserver alone cannot detect. Never a permanent RAF loop.
        clearTimeout(retry)
        retry = window.setTimeout(schedule, 100)
    }
    const schedule = () => {
        cancelAnimationFrame(frame)
        if (!stopped) frame = requestAnimationFrame(() => attempt())
    }
    window.addEventListener('wheel', interrupt, {capture: true, passive: true})
    window.addEventListener('touchstart', interrupt, {capture: true, passive: true})
    window.addEventListener('pointerdown', interrupt, {capture: true, passive: true})
    window.addEventListener('keydown', keyInterrupt, true)
    window.addEventListener('focusin', focusInterrupt, true)
    if (typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(schedule)
        observer.observe(content ?? (target === window ? document.body : target as HTMLElement))
    }
    timeout = window.setTimeout(end, RESTORE_TIMEOUT)
    schedule()
    return end
}

export interface HistoryScrollRestorationOptions {
    /** Account + organization identity; never use an access token. */
    scopeKey: string
    /** Current pathname and query, only used in memory to know when React committed. */
    routeKey: string
    enabled?: boolean
}

export function useHistoryScrollRestoration({scopeKey, routeKey, enabled = true}: HistoryScrollRestorationOptions) {
    const interruptedBeforeEnable = useRef(false)
    const cancelRef = useRef<(() => void) | undefined>(undefined)
    const [revision, setRevision] = useState(0)
    const handledRef = useRef<string>('')

    useLayoutEffect(() => {
        if (enabled) return
        const interrupt = () => { interruptedBeforeEnable.current = true }
        window.addEventListener('wheel', interrupt, {capture: true, passive: true})
        window.addEventListener('touchstart', interrupt, {capture: true, passive: true})
        window.addEventListener('pointerdown', interrupt, {capture: true, passive: true})
        window.addEventListener('keydown', interrupt, true)
        return () => {
            window.removeEventListener('wheel', interrupt, true)
            window.removeEventListener('touchstart', interrupt, true)
            window.removeEventListener('pointerdown', interrupt, true)
            window.removeEventListener('keydown', interrupt, true)
        }
    }, [enabled])

    useLayoutEffect(() => {
        if (!enabled) return
        activeScope = scopeKey
        handledRef.current = ''
        let mounted = true
        const stop = observeNavigationHistory(() => {
            cancelRef.current?.()
            captureFrozen = true
            // Next writes history inside useInsertionEffect. Notify React
            // after that commit instead of scheduling updates inside it.
            queueMicrotask(() => { if (mounted) setRevision(value => value + 1) })
        })
        const previous = window.history.scrollRestoration
        window.history.scrollRestoration = 'manual'
        const capture = () => saveCurrentPosition()
        beforeNavigation.add(capture)
        const onScroll = (event: Event) => {
            if (captureFrozen || (event.target !== window && event.target !== document &&
                !(event.target instanceof HTMLElement && event.target.matches('[data-editor-scroll]')))) return
            const surface = surfaceTarget()
            if (surface && (event.target === surface.target || (surface.target === window && event.target === document))) saveCurrentPosition(surface)
        }
        const onHide = () => { saveCurrentPosition(); persist() }
        const onPageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                cancelRef.current?.()
                captureFrozen = false
                saveCurrentPosition()
            }
        }
        window.addEventListener('scroll', onScroll, {capture: true, passive: true})
        window.addEventListener('pagehide', onHide)
        window.addEventListener('pageshow', onPageShow)
        document.addEventListener('visibilitychange', onHide)
        return () => {
            mounted = false
            cancelRef.current?.()
            persist()
            beforeNavigation.delete(capture)
            stop()
            window.history.scrollRestoration = previous
            window.removeEventListener('scroll', onScroll, true)
            window.removeEventListener('pagehide', onHide)
            window.removeEventListener('pageshow', onPageShow)
            document.removeEventListener('visibilitychange', onHide)
            if (activeScope === scopeKey) activeScope = undefined
        }
    }, [scopeKey, enabled])

    useLayoutEffect(() => {
        if (!enabled) return
        const key = getHistoryEntryKey()
        const committed = `${key}:${routeKey}`
        if (handledRef.current === committed) return
        // History and the router can commit in separate renders. Wait until the
        // shell reflects the current URL, without retaining URLs in storage.
        const search = new URLSearchParams(window.location.search).toString()
        const locationRoute = `${window.location.pathname}${search ? `?${search}` : ''}`
        if (routeKey !== locationRoute) return
        handledRef.current = committed
        cancelRef.current?.()
        const navigation = latestNavigation?.toKey === key ? latestNavigation : undefined
        const savedEditor = readPositions()[storageKey(scopeKey, key, 'editor')]
        const saved = savedEditor ?? readPositions()[storageKey(scopeKey, key, 'document')]
        const initialVisit = !navigation
        const historyVisit = navigation?.kind === 'pop' || initialVisit
        // Next owns fresh navigation, including Link/router scroll:false.
        // Fresh editors initialize their own surface in DocumentEditorFrame.
        const target = historyVisit && key && !interruptedBeforeEnable.current ? saved : undefined
        interruptedBeforeEnable.current = false
        captureFrozen = true
        if (!target) {
            captureFrozen = false
            return
        }
        cancelRef.current = restoreScrollPosition(window, target, {
            resolveTarget: () => {
                if (getLocalEditorReturnPoint()) return null
                const editor = getActiveDocumentEditor()
                if (historyVisit && savedEditor) return editor ? getEditorScrollOwner(editor) : null
                return getDocumentScrollOwner() === document && !editor ? window : null
            },
            onFinish: () => { captureFrozen = false },
        })
    }, [scopeKey, routeKey, enabled, revision])
}

const subscribeToClient = () => () => {}

function useHistoryViewEntry(key: string) {
    const ownerPath = useRef('')
    // Match SSR during hydration; ordinary client remounts read the visit
    // immediately so they never issue a query for the default page first.
    const client = useSyncExternalStore(subscribeToClient, () => true, () => false)
    const [entry, setEntry] = useState(() => client ? getHistoryEntryKey() : '')
    const entryRef = useRef(entry)
    useLayoutEffect(() => {
        // Next commits the destination URL in an insertion effect, after this
        // component rendered. Bind to the committed visit, not the opener.
        ownerPath.current = window.location.pathname
        let mounted = true
        const updateEntry = () => {
            if (window.location.pathname === ownerPath.current) {
                const key = getHistoryEntryKey()
                if (key) {
                    entryRef.current = key
                    setEntry(key)
                }
            }
        }
        updateEntry()
        const stop = observeNavigationHistory(() => queueMicrotask(() => { if (mounted) updateEntry() }))
        return () => { mounted = false; stop() }
    }, [key])
    return {entry, entryRef}
}

function readViewStates() {
    if (viewsLoaded || typeof window === 'undefined') return viewStates
    viewsLoaded = true
    try {
        const stored: unknown = JSON.parse(sessionStorage.getItem(VIEW_STORAGE_KEY) ?? '[]')
        if (Array.isArray(stored)) for (const item of stored.slice(-MAX_VIEW_STATES)) {
            if (Array.isArray(item) && typeof item[0] === 'string' && item[1] &&
                Number.isFinite(item[1].at) && Date.now() - item[1].at < MAX_AGE &&
                JSON.stringify(item[1].value)?.length <= 4096) viewStates.set(item[0], item[1])
        }
    } catch { /* Storage is optional. */ }
    return viewStates
}

// Validate stored JSON against this control's initial shape, not a fetched DTO.
function matchesViewShape(value: unknown, initial: unknown): boolean {
    if (typeof value !== typeof initial) return false
    if (typeof initial === 'number') return Number.isFinite(value)
    if (initial === null || typeof initial !== 'object') return value === null || ['string', 'boolean', 'number'].includes(typeof value)
    if (Array.isArray(initial)) return Array.isArray(value) && value.every(item => matchesViewShape(item, initial[0]))
    if (!value || Array.isArray(value)) return false
    return Object.keys(value).length === Object.keys(initial).length && Object.entries(initial).every(([key, item]) =>
        matchesViewShape((value as Record<string, unknown>)[key], item))
}

/** Small per-visit list controls only; never pass records, credentials or form drafts. */
export function useHistoryViewState<T>(key: string, initialValue: T | (() => T), scopeKey: string): [T, (value: T | ((previous: T) => T)) => void] {
    const {entry, entryRef} = useHistoryViewEntry(key)
    const identity = storageKey(scopeKey, entry, `view:${key}`)
    const readValue = (): T => {
        const initial = typeof initialValue === 'function' ? (initialValue as () => T)() : initialValue
        const saved = readViewStates().get(identity)
        return saved && Date.now() - saved.at < MAX_AGE && matchesViewShape(saved.value, initial) ? saved.value as T : initial
    }
    const [state, setState] = useState(() => ({identity, value: readValue()}))
    // Reset synchronously on identity changes, so pagination/filter effects do
    // not run once with state belonging to a different account or visit.
    if (state.identity !== identity) setState({identity, value: readValue()})
    const value = state.identity === identity ? state.value : readValue()
    const valueRef = useRef(value)
    const metadataRef = useRef({key, scopeKey})
    valueRef.current = value
    metadataRef.current = {key, scopeKey}
    const setter = useCallback((next: T | ((previous: T) => T)) => {
        const identity = storageKey(metadataRef.current.scopeKey, entryRef.current, `view:${metadataRef.current.key}`)
        const resolved = typeof next === 'function' ? (next as (previous: T) => T)(valueRef.current) : next
        valueRef.current = resolved
        viewStates.delete(identity)
        viewStates.set(identity, {value: resolved, at: Date.now()})
        while (viewStates.size > MAX_VIEW_STATES) viewStates.delete(viewStates.keys().next().value!)
        try {
            const recent = [...viewStates].filter(([, item]) => Date.now() - item.at < MAX_AGE && JSON.stringify(item.value).length <= 4096)
            sessionStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify(recent))
        } catch { /* Memory remains available when storage is unavailable. */ }
        setState({identity, value: resolved})
    }, [entryRef])
    return [value, setter]
}

/** Opt in a real nested scroll viewport; no discovery/scanning of arbitrary DOM. */
export function useHistoryScrollArea<T extends HTMLElement>(key: string, scopeKey: string): RefCallback<T> {
    const {entry} = useHistoryViewEntry(key)
    const identity = storageKey(scopeKey, entry, `region:${key}`)
    return useCallback((element: T | null) => {
        if (!element) return
        let restoring = true
        const available = () => element.isConnected && !element.closest('[hidden], [inert]')
        const save = () => {
            // Layout changes and Activity hiding can clamp a viewport to zero.
            // Save only an interactive viewport, not a hidden background tree.
            if (!restoring && entry === getHistoryEntryKey() && available() && element.getClientRects().length) savePosition(identity, readScrollPosition(element))
        }
        const saved = readPositions()[identity]
        const cancel = restoreScrollPosition(element, saved ?? {x: 0, y: 0}, {
            isReady: available,
            onFinish: () => { restoring = false },
            content: element.firstElementChild ?? element,
        })
        element.addEventListener('scroll', save, {passive: true})
        beforeNavigation.add(save)
        return () => {
            cancel()
            element.removeEventListener('scroll', save)
            beforeNavigation.delete(save)
        }
    }, [identity, entry])
}
